import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  SIGNUP_BOOK_A_CALL_PAGE,
  SIGNUP_ERROR_PAGE,
  signupPagePath,
  type SignupPageId,
} from '../off-path-pages';
import { parseLegacySignupParams } from '../services/legacy-params';
import { nextSignupStep, signupPathFor, type SignupPath, type SignupStepId } from '../steps';

/**
 * Step navigation for the signup flow. Every move replaces the current history
 * entry and never pushes, so the whole flow occupies the one entry the visitor
 * arrived on and browser Back leaves the flow (the legacy app has no back
 * navigation either). The original query string rides along so the legacy
 * params survive a reload on any step, and the path (employer link or not) is
 * read from it on every step, so a reload stays on the same path. The query
 * is read when the move happens, not when the callback was made, so a step
 * that finishes an async call after a language switch carries the new `lng`
 * rather than the old one.
 */
export function useSignupNavigation(): {
  goToStep: (page: SignupPageId) => void;
  /**
   * Like `goToStep`, with `query` in place of the current query string: for a
   * move that changes the sponsor link, such as resuming a returning member.
   */
  goToStepWithQuery: (page: SignupPageId, query: string) => void;
  /** Moves to the step after `current` on this link's path; does nothing on the last step. */
  goToNextStep: (current: SignupStepId) => void;
  /** The error screen, for every failure production sends to its own. */
  goToErrorScreen: () => void;
  /** The "Book a call" page. */
  goToBookACall: () => void;
  path: SignupPath;
} {
  const router = useRouter();
  const query = useSearchParams().toString();
  const path = useMemo(() => signupPathFor(parseLegacySignupParams(query)), [query]);
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);
  const goToStep = useCallback(
    (page: SignupPageId) => {
      const target = signupPagePath(page);
      const current = queryRef.current;
      router.replace(current ? `${target}?${current}` : target);
    },
    [router],
  );
  const goToStepWithQuery = useCallback(
    (page: SignupPageId, query: string) => {
      const target = signupPagePath(page);
      router.replace(query ? `${target}?${query}` : target);
    },
    [router],
  );
  const goToNextStep = useCallback(
    (current: SignupStepId) => {
      const next = nextSignupStep(current, path);
      if (next !== null) goToStep(next);
    },
    [goToStep, path],
  );
  const goToErrorScreen = useCallback(() => goToStep(SIGNUP_ERROR_PAGE), [goToStep]);
  const goToBookACall = useCallback(() => goToStep(SIGNUP_BOOK_A_CALL_PAGE), [goToStep]);
  return { goToStep, goToStepWithQuery, goToNextStep, goToErrorScreen, goToBookACall, path };
}
